import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileDown, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common";
import { PnlTab } from "@/components/accounts/PnlTab";
import { ExpensesTab } from "@/components/accounts/ExpensesTab";
import { BookTab } from "@/components/accounts/BookTab";
import { PayrollTab } from "@/components/accounts/PayrollTab";
import { api, errMsg } from "@/lib/api";
import { todayIST } from "@/lib/format";

export default function Accounts() {
  const { user } = useAuth();
  const [month, setMonth] = useState(todayIST().slice(0, 7));
  const [d, setD] = useState({});
  const [categories, setCategories] = useState([]);
  const [sending, setSending] = useState(false);
  const load = async () => {
    const p = { params: { month } };
    try {
      const [pnl, exp, cash, bank, pay] = await Promise.all([api.get("/accounts/pnl", p), api.get("/accounts/expenses", p),
        api.get("/accounts/book", { params: { month, book: "cash" } }), api.get("/accounts/book", { params: { month, book: "bank" } }), api.get("/payroll", p)]);
      setD({ pnl: pnl.data, exp: exp.data, cash: cash.data, bank: bank.data, pay: pay.data });
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { api.get("/accounts/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async () => {
    try { const r = await api.get("/accounts/report.pdf", { params: { month }, responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `NexFresh_Report_${month}.pdf`; a.click(); }
    catch (e) { toast.error("Could not create the report"); }
  };
  const testReport = async () => {
    setSending(true);
    try { const r = await api.post("/accounts/report/send", null, { params: { month } }); toast.success(`Test report for ${month} emailed to ${r.data.to}`); }
    catch (e) { toast.error(errMsg(e)); }
    await download();
    setSending(false);
  };
  return (
    <div data-testid="accounts-page">
      <PageHeader eyebrow="Finance" title="Accounts" subtitle="Expenses, income, monthly profit & loss, cash and bank books, and staff salaries.">
        <div className="flex flex-wrap gap-2">
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-40 bg-white" data-testid="accounts-month" />
          <Button variant="outline" onClick={download} className="h-9 rounded-full" data-testid="report-download-btn"><FileDown className="mr-1 h-4 w-4" />Monthly PDF</Button>
          {user.role === "admin" && <Button onClick={testReport} disabled={sending} className="h-9 rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="report-test-send-btn">{sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}Send me a test report now</Button>}
        </div>
      </PageHeader>
      <Tabs defaultValue="pnl">
        <TabsList className="mb-4 flex-wrap bg-[#FAF4E8]">
          <TabsTrigger value="pnl" data-testid="tab-pnl">Profit & loss</TabsTrigger>
          <TabsTrigger value="exp" data-testid="tab-expenses">Expenses & income</TabsTrigger>
          <TabsTrigger value="cash" data-testid="tab-cash-book">Cash book</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank-book">Bank book</TabsTrigger>
          <TabsTrigger value="pay" data-testid="tab-salaries">Salaries</TabsTrigger>
        </TabsList>
        <TabsContent value="pnl"><PnlTab pnl={d.pnl} /></TabsContent>
        <TabsContent value="exp"><ExpensesTab rows={d.exp || []} categories={categories} onChanged={load} /></TabsContent>
        <TabsContent value="cash"><BookTab data={d.cash} /></TabsContent>
        <TabsContent value="bank"><BookTab data={d.bank} /></TabsContent>
        <TabsContent value="pay"><PayrollTab data={d.pay} onChanged={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
